/**
 * De qué tienda --y de qué almacén-- es la caja que está abierta.
 *
 * ## El defecto que la hizo falta
 *
 * `getActiveLocationCode` empezaba por `if (!apertura?.TPV) return null`, y la
 * apertura guarda su tienda como una **copia**: la escribe el formulario al
 * crearla, leyéndola de la caja elegida. Cuando esa copia sale vacía, la
 * apertura queda muda para todo lo que dependa de la tienda.
 *
 * Y no se nota, porque casi nada la mira. La cabecera enseña la caja y el
 * turno, así que dice «CAJA 1 CCMDS · Turno 1» tan contenta; el arqueo del
 * cierre tampoco la usa. La única que se planta es la sincronización de
 * productos, y lo hacía con un mensaje que decía **«No hay caja abierta»**
 * delante de una caja abierta. Quien lo leía abría otra caja --que nacía con
 * el mismo hueco-- y volvía a lo mismo.
 *
 * ## Lo que hace en su lugar
 *
 * La tienda se **deduce** en vez de depender de la copia: la apertura sabe su
 * caja, y la caja sabe su tienda. Si la copia está, se usa; si no, se busca por
 * la caja. Así se arreglan también las aperturas que ya están guardadas mal,
 * sin pedirle a nadie que cierre y vuelva a abrir.
 *
 * Y cuando de verdad no se puede, dice **cuál** de los cuatro motivos es. No
 * son intercambiables: uno se resuelve abriendo caja, otro sincronizando el
 * maestro de cajas, y otro en Business Central.
 */

/** Los motivos por los que no hay almacén, y qué hacer con cada uno. */
export const MOTIVOS = {
  SIN_CAJA: "sin-caja",
  CAJA_SIN_TIENDA: "caja-sin-tienda",
  TIENDA_DESCONOCIDA: "tienda-desconocida",
  TIENDA_SIN_ALMACEN: "tienda-sin-almacen",
};

/**
 * @param {object|null} apertura La apertura activa, o `null` si no hay ninguna.
 * @param {(no: string) => Promise<object|null|undefined>} buscarCaja Por número de caja.
 * @param {(no: string) => Promise<object|null|undefined>} buscarTienda Por número de tienda.
 * @returns {Promise<{locationCode: string|null, motivo: string|null, tienda: string|null, caja: string|null}>}
 */
export const resolverAlmacenDeLaCaja = async (apertura, buscarCaja, buscarTienda) => {
  const nada = (motivo, extra = {}) => ({
    locationCode: null,
    motivo,
    tienda: null,
    caja: null,
    ...extra,
  });

  if (!apertura) return nada(MOTIVOS.SIN_CAJA);

  const caja = String(apertura.Caja ?? "").trim() || null;
  let tienda = String(apertura.TPV ?? "").trim() || null;

  // La copia no estaba: se deduce por la caja, que es quien de verdad sabe a
  // qué tienda pertenece.
  if (!tienda && caja) {
    const registroDeCaja = await buscarCaja(caja);
    tienda = String(registroDeCaja?.TPV ?? "").trim() || null;
  }

  if (!tienda) return nada(MOTIVOS.CAJA_SIN_TIENDA, { caja });

  const registroDeTienda = await buscarTienda(tienda);
  if (!registroDeTienda) return nada(MOTIVOS.TIENDA_DESCONOCIDA, { caja, tienda });

  const locationCode = String(registroDeTienda.locationCode ?? "").trim() || null;
  if (!locationCode) return nada(MOTIVOS.TIENDA_SIN_ALMACEN, { caja, tienda });

  return { locationCode, motivo: null, tienda, caja };
};

/** Qué decirle a quien lo intentó, según el motivo. */
export const explicarFaltaDeAlmacen = ({ motivo, caja, tienda }) => {
  switch (motivo) {
    case MOTIVOS.SIN_CAJA:
      return "No hay ninguna caja abierta. Abra una caja para poder sincronizar los productos.";
    case MOTIVOS.CAJA_SIN_TIENDA:
      return `La caja abierta${caja ? ` (${caja})` : ""} no tiene una tienda asignada. Sincronice el maestro de Cajas y vuelva a intentarlo.`;
    case MOTIVOS.TIENDA_DESCONOCIDA:
      return `La tienda ${tienda} de la caja abierta no está en el maestro de Tiendas de este equipo. Sincronice Tiendas y vuelva a intentarlo.`;
    case MOTIVOS.TIENDA_SIN_ALMACEN:
      return `La tienda ${tienda} no tiene almacén configurado en Business Central, así que no hay stock por tienda que traer.`;
    default:
      return "No se pudo determinar el almacén de la caja abierta.";
  }
};
